import React, { useState } from "react";
import { api, API, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Upload, Download, Database, CheckCircle2, AlertTriangle } from "lucide-react";

const ENTITIES = [
  { key: "suppliers", label: "Suppliers" },
  { key: "customers", label: "Customers" },
  { key: "products", label: "Products" },
];

export default function DataImport() {
  const [entity, setEntity] = useState("suppliers");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/${entity}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.inserted} record(s)${data.error_count ? `, ${data.error_count} error(s)` : ""}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Database className="h-6 w-6 text-[#12346B]" /> Data Migration</h1>
        <p className="text-sm text-slate-500 mt-0.5">Bulk import master data from CSV with validation and audit. Download a template, fill it, and upload.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 max-w-2xl">
        <div className="flex flex-wrap gap-2 mb-4">
          {ENTITIES.map((e) => (
            <button key={e.key} onClick={() => { setEntity(e.key); setResult(null); }} data-testid={`import-entity-${e.key}`}
              className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${entity === e.key ? "bg-[#12346B] text-white border-[#12346B]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {e.label}
            </button>
          ))}
        </div>

        <a href={`${API}/import/template/${entity}`} target="_blank" rel="noreferrer" data-testid="btn-download-template"
          className="inline-flex items-center gap-1.5 text-sm text-[#12346B] font-semibold hover:underline mb-4">
          <Download className="h-4 w-4" /> Download {entity} CSV template
        </a>

        <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
          <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} data-testid="import-file-input" className="hidden" id="csv-file" />
          <label htmlFor="csv-file" className="cursor-pointer inline-flex flex-col items-center gap-2">
            <Upload className="h-8 w-8 text-slate-400" />
            <span className="text-sm text-slate-600">{file ? file.name : "Click to choose a CSV file"}</span>
          </label>
        </div>

        <Button className="mt-4 w-full bg-[#12346B] hover:bg-[#12346B]/90" onClick={upload} disabled={busy || !file} data-testid="btn-import-upload">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} Import {entity}
        </Button>

        {result && (
          <div className="mt-5 border-t border-slate-100 pt-4" data-testid="import-result">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> {result.inserted} record(s) imported</div>
            {result.error_count > 0 && (
              <div className="mt-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-rose-600"><AlertTriangle className="h-4 w-4" /> {result.error_count} row(s) skipped</div>
                <ul className="mt-1 text-xs text-slate-500 space-y-0.5 max-h-40 overflow-auto">
                  {result.errors.map((er, i) => <li key={`${er.row}-${i}`}>Row {er.row}: {er.error}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
